// File: src/MathTools/ParametricGraphTool.js
//----------------------------------------------------------
// A minimal parametric plotter using D3.
// Props:
//   xEqn, yEqn: strings such as "t^2 - 1", "2*t", etc.
//   tMin, tMax: numeric range for parameter t
//   steps: how many sample points
//   width, height: SVG size
//   showAxes: optional boolean (default false)
//   style: optional inline style

import React, { useRef, useEffect } from "react";
import * as d3 from "d3";

export default function ParametricGraphTool({
  xEqn = "t",
  yEqn = "t^2",
  tMin = -5,
  tMax = 5,
  steps = 100,
  width = 400,
  height = 300,
  showAxes = false,
  style = {}
}) {
  const svgRef = useRef(null);

  // 1) Build function for x(t) and y(t)
  function buildFn(expr) {
    // Convert ^ to ** for exponent
    const safeExpr = expr.replace(/\^/g, "**");
    try {
      // eslint-disable-next-line no-new-func
      return new Function("t", `return (${safeExpr});`);
    } catch (err) {
      console.error("Parametric parse error:", err);
      // fallback: f(t)=0
      return () => 0;
    }
  }

  useEffect(() => {
    const fx = buildFn(xEqn);
    const fy = buildFn(yEqn);
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // 2) Sample param t from tMin..tMax
    const dt = (tMax - tMin) / steps;
    const points = [];
    for (let i = 0; i <= steps; i++) {
      const t = tMin + i * dt;
      const xx = fx(t);
      const yy = fy(t);
      // skip if infinite
      if (!Number.isFinite(xx) || !Number.isFinite(yy)) continue;
      points.push({ x: xx, y: yy });
    }
    if (points.length < 2) {
      svg.append("text").attr("x", 10).attr("y", 20).text("No valid data.");
      return;
    }

    // 3) Determine xMin..xMax, yMin..yMax from points
    const xVals = points.map(p => p.x);
    const yVals = points.map(p => p.y);
    const xMinD = d3.min(xVals), xMaxD = d3.max(xVals);
    const yMinD = d3.min(yVals), yMaxD = d3.max(yVals);

    // add small padding
    const xPad = 0.1 * (xMaxD - xMinD);
    const yPad = 0.1 * (yMaxD - yMinD);

    const finalXmin = xMinD - xPad;
    const finalXmax = xMaxD + xPad;
    const finalYmin = yMinD - yPad;
    const finalYmax = yMaxD + yPad;

    // 4) Scales
    const xScale = d3.scaleLinear()
      .domain([finalXmin, finalXmax])
      .range([50, width - 10]); // left margin 50, right margin 10

    const yScale = d3.scaleLinear()
      .domain([finalYmin, finalYmax])
      .range([height - 30, 10]); // bottom margin 30, top margin 10

    // 5) Axes if showAxes
    if (showAxes) {
      const xAxis = d3.axisBottom(xScale).ticks(6);
      svg.append("g")
        .attr("transform", `translate(0, ${yScale(finalYmin)})`)
        .call(xAxis);

      const yAxis = d3.axisLeft(yScale).ticks(6);
      svg.append("g")
        .attr("transform", `translate(${xScale(finalXmin)}, 0)`)
        .call(yAxis);
    }

    // 6) Build a line path
    const lineGen = d3.line()
      .x(d => xScale(d.x))
      .y(d => yScale(d.y));

    svg.append("path")
      .datum(points)
      .attr("fill", "none")
      .attr("stroke", "blue")
      .attr("stroke-width", 2)
      .attr("d", lineGen);

    // (Optional) label the eqns
    svg.append("text")
      .attr("x", 60)
      .attr("y", 25)
      .style("fontSize", "14px")
      .style("fill", "#333")
      .text(`x(t)=${xEqn}, y(t)=${yEqn}`);
  }, [xEqn, yEqn, tMin, tMax, steps, width, height, showAxes]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{ border: "1px solid #ccc", background: "#fff", ...style }}
    />
  );
}
